// src/resources/Vehicles/VehicleExcelExport.js
import React from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { calculateRemainingFinance, vehicleStatusAsOf, getFinalInstallmentEnd, formatDeferredInstallments, getDeferredInstallmentCount, getDeferredInstallmentsTotal } from "./vehicleFinance";

export default function VehicleExcelExport({ vehicles = [], asOfDate }) {
  const asOf = asOfDate ? new Date(asOfDate + "T00:00:00") : new Date();
  // ----------------- Helpers -----------------
  const formatDate = (d) => {
    if (!d) return "";
    const date = d.seconds ? new Date(d.seconds * 1000) : new Date(d);
    if (isNaN(date)) return "";
    return date.toLocaleDateString("en-GB");
  };

  const calculateRemaining = (v) => calculateRemainingFinance(v, asOf);

  const isCashCar = (v) => !["FINANCED", "DEFERRED"].includes(vehicleStatusAsOf(v, asOf));

  const vehicleNumberSortValue = (value) => {
    const text = String(value || "").trim();
    const match = text.match(/\d+/);
    return match ? Number(match[0]) : Number.MAX_SAFE_INTEGER;
  };

  const sortByVehicleNumber = (a, b) => {
    const aNo = vehicleNumberSortValue(a?.vehicleNumber);
    const bNo = vehicleNumberSortValue(b?.vehicleNumber);

    if (aNo !== bNo) return aNo - bNo;

    return String(a?.vehicleNumber || "").localeCompare(
      String(b?.vehicleNumber || ""),
      undefined,
      { numeric: true, sensitivity: "base" }
    );
  };

  // ---------------------------------------------------
  //                HANDLE EXCEL EXPORT
  // ---------------------------------------------------
  const handleDownload = async () => {
    if (!vehicles.length) {
      alert("No vehicles to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const today = new Date().toLocaleDateString("en-GB");
    const sortedVehicles = [...vehicles].sort(sortByVehicleNumber);

    // Detect company filter
    const companySet = [...new Set(sortedVehicles.map((v) => v.company || ""))];
    let companyTitle =
      companySet.length === 1
        ? companySet[0]
        : "Comfort Line & Lolia Limousine";

    // =====================================================
    // SHEET 1 — SUMMARY (Corporate Style)
    // =====================================================
    const summarySheet = workbook.addWorksheet("Summary");

    summarySheet.columns = [
      { header: "Category", width: 45 },
      { header: "Value", width: 40 },
    ];

    // Title Row
    const titleRow = summarySheet.addRow([
      `VEHICLE FINANCE SUMMARY — ${companyTitle}`,
      `Generated: ${today}`,
    ]);
    titleRow.font = { bold: true, size: 16, color: { argb: "1F4E78" } };

    summarySheet.addRow([]);

    // Section: Remaining Finance Summary
    const remTitle = summarySheet.addRow([
      "Remaining Finance Summary (By Mortgage Bank)",
      "",
    ]);
    remTitle.font = { bold: true, size: 13 };
remTitle.fill = {

      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "E8EEF7" },
    };

    const bankRemainingMap = {};
    sortedVehicles.forEach((v) => {
      const rem = calculateRemainingFinance(v, asOf);
      const bank = v.mortgageFrom || "Unknown Bank";
      bankRemainingMap[bank] = (bankRemainingMap[bank] || 0) + rem;
    });

    Object.keys(bankRemainingMap)
      .sort()
      .forEach((bank) => {
        const r = summarySheet.addRow([bank, bankRemainingMap[bank]]);
        r.font = { size: 12 };
      });

    summarySheet.addRow([]);

    // Section: Installment Deduction Summary
    const instTitle = summarySheet.addRow([
      "Installment Deduction Summary (By Date)",
      "",
    ]);
    instTitle.font = { bold: true, size: 13 };
    instTitle.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "E8EEF7" },
    };

    const bankGroup = {};
    sortedVehicles
      .filter((v) => !isCashCar(v))
      .forEach((v) => {
        const bank = v.installmentBank || "Unknown Bank";
        const start = v.installmentStart
          ? new Date(
              v.installmentStart.seconds
                ? v.installmentStart.seconds * 1000
                : v.installmentStart
            )
          : null;

        const day = start && !isNaN(start.getTime()) ? start.getDate() : 1;
        if (!bankGroup[bank]) bankGroup[bank] = {};
        bankGroup[bank][day] =
          (bankGroup[bank][day] || 0) + Number(v.monthlyInstallment || 0);
      });

    Object.keys(bankGroup)
      .sort()
      .forEach((bank) => {
        const bRow = summarySheet.addRow([bank]);
        bRow.font = { bold: true };

        Object.keys(bankGroup[bank])
          .map(Number)
          .sort((a, b) => a - b)
          .forEach((day) => {
            summarySheet.addRow([`  ${day} of Month`, bankGroup[bank][day]]);
          });

        summarySheet.addRow([]);
      });

    // Borders
    summarySheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        cell.alignment = { vertical: "middle" };
      });
    });

    // =====================================================
    // SHEET 2 — ALL VEHICLES
    // =====================================================
    const listSheet = workbook.addWorksheet("All Vehicles");

    listSheet.columns = [
      { header: "S/N", width: 6 },
      { header: "Company", width: 20 },
      { header: "Vehicle No.", width: 15 },
      { header: "Type / Model", width: 30 },
      { header: "Year", width: 8 },
      { header: "Mortgage From", width: 20 },
      { header: "Installment Bank", width: 20 },
      { header: "Monthly (AED)", width: 15 },
      { header: "Start", width: 12 },
      { header: "Original End", width: 12 },
      { header: "Final End", width: 12 },
      { header: "Deferred Months", width: 24 },
      { header: "Deferred Count", width: 15 },
      { header: "Deferred Amount", width: 18 },
      { header: "Bank Charges", width: 15 },
      { header: "Settlement Date", width: 15 },
      { header: "Settlement Amount", width: 18 },
      { header: "Remaining (AED)", width: 18 },
      { header: "Insurance", width: 12 },
      { header: "Registration", width: 12 },
      { header: "Status", width: 12 },
      { header: "Type", width: 12 },
    ];

    // Header styling
    listSheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "1F4E78" },
      };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Rows
    sortedVehicles.forEach((v, idx) => {
      const rem = calculateRemaining(v);
      const cash = isCashCar(v);

      const status = vehicleStatusAsOf(v, asOf);
      const displayStatus = cash ? "Cash" : status;
      const row = listSheet.addRow([
        idx + 1,
        v.company || "",
        v.vehicleNumber || "",
        v.vehicleType || "",
        v.vehicleYear || "",
        cash ? "" : v.mortgageFrom || "",
        cash ? "" : v.installmentBank || "",
        cash ? "" : Number(v.monthlyInstallment) || 0,
        cash ? "" : formatDate(v.installmentStart),
        cash ? "" : formatDate(v.installmentEnd),
        cash ? "" : formatDate(getFinalInstallmentEnd(v)),
        cash ? "" : formatDeferredInstallments(v),
        cash ? "" : getDeferredInstallmentCount(v),
        cash ? "" : getDeferredInstallmentsTotal(v),
        cash ? "" : Number(v.bankCharges) || 0,
        cash ? "" : formatDate(v.settlementDate),
        cash ? "" : Number(v.settlementAmount) || 0,
        cash ? 0 : rem,
        formatDate(v.insuranceExpiry),
        formatDate(v.registrationExpiry),
        displayStatus,
        cash ? "Cash" : "Financed",
      ]);

      // Style rows
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        cell.alignment = { vertical: "middle" };
      });

      // Cash car highlight (soft corporate green)
      if (cash) {
        row.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "E2F7E2" },
          };
        });
      }
    });

    // =====================================================
    // WRITE FILE
    // =====================================================
    const buf = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `Vehicles_Report_${companyTitle}.xlsx`);
  };

  return (
    <button
      type="button"
      onClick={handleDownload}
      style={{
        background: "#1565c0",
        color: "white",
        border: "none",
        padding: "6px 12px",
        borderRadius: 6,
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 500,
      }}
    >
      📊 Download Excel
    </button>
  );
}
